import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";

// Assume these icons are imported from an icon library
import {
  BoxCubeIcon,
  CalenderIcon,
  ChevronDownIcon,
  GridIcon,
  ListIcon,
  PageIcon,
  PieChartIcon,
  PlugInIcon,
  TableIcon,
  UserCircleIcon,
} from "../icons";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext"; // Import useAuth
import FooterMobile from "../components/layout/FooterMobile";
import { useErrorReport } from "../context/ErrorReportContext";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  activePaths?: string[]; // Highlight item when current pathname starts with any of these
  requiredPermission?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean; requiredPermission?: string }[];
};

// Navigation items configuration with translation keys - Reorganized for better UX
const getNavItems = (t: (key: string) => string): NavItem[] => [
  // Primary daily tasks - always visible
  {
    icon: <GridIcon />,
    name: t('navigation.dashboard'),
    path: "/",
    requiredPermission: "guia",
  },
  {
    icon: <CalenderIcon />,
    name: t('navigation.calendar'),
    path: "/calendar",
    requiredPermission: "guia",
  },
  {
    icon: <TableIcon />,
    name: t('navigation.services'),
    path: "/servicios",
    activePaths: ["/servicios"],
    requiredPermission: "guia",
  },
  // FACTURACIÓN — tabs internas: Historial / Pendientes / Nueva / Plantillas
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/>
      <line x1="9" y1="17" x2="13" y2="17"/>
    </svg>,
    name: "Facturación",
    path: "/facturacion/historial",
    activePaths: ["/facturacion/historial", "/facturacion/pendientes", "/facturacion/new", "/facturacion/editar", "/facturacion/preview", "/facturacion/templates"],
    requiredPermission: "guia",
  },
  // FISCAL — tabs internas: Resumen Trimestre / Gastos / Tarifas
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>,
    name: "Fiscal",
    path: "/facturacion/cierre-trimestre",
    activePaths: ["/facturacion/cierre-trimestre", "/facturacion/tarifas", "/gastos"],
    requiredPermission: "guia",
  },
  {
    icon: <UserCircleIcon />,
    name: t('navigation.clients'),
    path: "/clientes",
    requiredPermission: "guia",
  },
  // TEMPORARILY HIDDEN - Files page
  // {
  //   icon: <PageIcon />,
  //   name: t('navigation.files'),
  //   path: "/archivos",
  //   requiredPermission: "guia",
  // },
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    name: t('navigation.analytics'),
    path: "/analytics",
    requiredPermission: "guia",
  },
  
  // Referrals - now a main navigation item
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M20 8v6M23 11h-6" />
    </svg>,
    name: t('navigation.referrals'),
    path: "/referidos",
    requiredPermission: "guia",
  },

  
  
  
  // Help & Support - simplified
  {
    icon: (
      <svg 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    name: t('navigation.helpSupport'),
    requiredPermission: "guia",
    subItems: [
      { name: t('navigation.helpCenter'), path: "/support/help-center", requiredPermission: "guia" },
      { name: t('navigation.aiAssistant'), path: "/support/chat", requiredPermission: "guia" },
      { name: t('navigation.supportTickets'), path: "/support/tickets", requiredPermission: "guia" },
    ],
  },
  // El panel de administración vive ahora en admin.guidemeapp.es (app separada)
];

const othersItems: NavItem[] = [];

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const { user } = useAuth(); // Get user from AuthContext
  const { t } = useTranslation();
  const location = useLocation();
  const { openModal } = useErrorReport();

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  const isParentActive = useCallback(
    (nav: NavItem) => {
      if (nav.activePaths && nav.activePaths.length > 0) {
        return nav.activePaths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
      }
      if (nav.path) return location.pathname === nav.path;
      return false;
    },
    [location.pathname]
  );

  useEffect(() => {
    let submenuMatched = false;
    ["main", "others"].forEach((menuType) => {
      const items = menuType === "main" ? getNavItems(t) : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                type: menuType as "main" | "others",
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, t]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => {
    const filteredItems = items.filter(nav => {
      if (!nav.requiredPermission) return true; // No permission required, always show
      if (!user) return false; // No user, hide item

      // Check if user has the required permission
      // Superadmin can access everything
      if (user.permisos === "superadmin") {
        return true;
      }
      if (nav.requiredPermission === "admin" && user.permisos === "admin") {
        return true;
      }
      if (nav.requiredPermission === "guia" && (user.permisos === "guia" || user.permisos === "admin")) {
        return true;
      }
      return false;
    });

    return (
      <ul className="flex flex-col gap-4">
        {filteredItems.map((nav, index) => (
          <li key={`${menuType}-${index}-${nav.path || nav.name}`}>
            {nav.subItems ? (
              <button
                onClick={() => handleSubmenuToggle(index, menuType)}
                className={`menu-item group text-left ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-active"
                    : "menu-item-inactive"
                } cursor-pointer`}
              >
                <span
                  className={`menu-item-icon-size ${
                    openSubmenu?.type === menuType && openSubmenu?.index === index
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
                {(isExpanded || isHovered || isMobileOpen) && (
                  <ChevronDownIcon
                    className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                      openSubmenu?.type === menuType &&
                      openSubmenu?.index === index
                        ? "rotate-180 text-brand-500"
                        : ""
                    }`}
                  />
                )}
              </button>
            ) : (
              nav.path && (
                <Link
                  to={nav.path}
                  className={`menu-item group ${
                    isParentActive(nav) ? "menu-item-active" : "menu-item-inactive"
                  }`}
                >
                  <span
                    className={`menu-item-icon-size ${
                      isParentActive(nav)
                        ? "menu-item-icon-active"
                        : "menu-item-icon-inactive"
                    }`}
                  >
                    {nav.icon}
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className="menu-item-text">{nav.name}</span>
                  )}
                </Link>
              )
            )}
            {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
              <div
                ref={(el) => {
                  subMenuRefs.current[`${menuType}-${index}`] = el;
                }}
                className="overflow-hidden transition-all duration-300 relative z-20"
                style={{
                  height:
                    openSubmenu?.type === menuType && openSubmenu?.index === index
                      ? `${subMenuHeight[`${menuType}-${index}`]}px`
                      : "0px",
                }}
              >
                <ul className="mt-2 space-y-1 ml-9">
                  {nav.subItems
                    .filter(subItem => {
                      if (!subItem.requiredPermission) return true;
                      if (!user) return false;
                      
                      // Superadmin can access everything
                      if (user.permisos === "superadmin") {
                        return true;
                      }
                      if (subItem.requiredPermission === "admin" && user.permisos === "admin") {
                        return true;
                      }
                      if (subItem.requiredPermission === "guia" && (user.permisos === "guia" || user.permisos === "admin")) {
                        return true;
                      }
                      return false;
                    })
                    .map((subItem, subIndex) => (
                      <li key={`${menuType}-${index}-sub-${subIndex}-${subItem.path}`}>
                        <Link
                          to={subItem.path}
                          className={`menu-dropdown-item ${
                            isActive(subItem.path)
                              ? "menu-dropdown-item-active"
                              : "menu-dropdown-item-inactive"
                          }`}
                        >
                          {subItem.name}
                          <span className="flex items-center gap-1 ml-auto">
                            {subItem.new && (
                              <span
                                className={`ml-auto ${
                                  isActive(subItem.path)
                                    ? "menu-dropdown-badge-active"
                                    : "menu-dropdown-badge-inactive"
                                } menu-dropdown-badge`}
                              >
                                Nuevo
                              </span>
                            )}
                            {subItem.pro && (
                              <span
                                className={`ml-auto ${
                                  isActive(subItem.path)
                                    ? "menu-dropdown-badge-active"
                                    : "menu-dropdown-badge-inactive"
                                } menu-dropdown-badge`}
                              >
                                Pro
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-4 left-0 bg-white dark:bg-gray-900 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 dark:border-transparent
        ${
          isExpanded || isMobileOpen
            ? "w-[280px]"
            : isHovered
            ? "w-[280px]"
            : "w-[80px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo - Desktop only (hidden on mobile to save space) */}
      <div
        className={`py-4 flex hidden lg:flex items-center ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/" className="flex items-center">
          {isExpanded || isHovered ? (
            <>
              <img
                className="dark:hidden"
                src="/images/logo/guideme-logo.png"
                alt="GuideMe Logo"
                width={140}
                height={36}
              />
              <img
                className="hidden dark:block"
                src="/images/logo/guideme-dark.png"
                alt="GuideMe Logo"
                width={140}
                height={36}
              />
            </>
          ) : (
            <img
              src="/images/logo/logo-icon.svg"
              alt="GuideMe Logo"
              width={30}
              height={30}
            />
          )}
        </Link>
      </div>
      
      <div className="flex flex-col flex-1 min-h-0">
        {/* Scrollable area including navigation and footer */}
        <div className="flex-1 overflow-y-auto duration-300 ease-linear custom-scrollbar pt-4">
          <nav className="mb-6 relative z-30">
            <div className="flex flex-col gap-4">
              <div>
                {renderMenuItems(getNavItems(t), "main")}
              </div>
            </div>
          </nav>


          {/* Footer content now inside scrollable area */}
          <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700">
            
            {/* Footer Section - Always visible but adaptive */}
            <div className="space-y-2 pb-4">
              {/* Report Button */}
              {(isExpanded || isHovered || isMobileOpen) ? (
                <button
                  onClick={openModal}
                  className="w-full flex items-center px-3 py-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 border border-gray-200 dark:border-gray-600"
                  title="Reportar problema"
                >
                  <svg className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Reportar problema
                </button>
              ) : (
                <div className="flex justify-center">
                  <button
                    onClick={openModal}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200"
                    title="Reportar problema"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </button>
                </div>
              )}
              
              
              {/* Mobile Footer - Only show on mobile */}
              {isMobileOpen && (
                <div className="lg:hidden relative z-10 border-t border-gray-200 dark:border-gray-700 pt-2">
                  <FooterMobile />
                </div>
              )}
            </div>
          </div>

        </div>
        
        {/* Fixed Copyright at bottom - Outside scrollable area */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 py-2">
          {(isExpanded || isHovered || isMobileOpen) ? (
            <div className="px-4 flex items-center justify-center">
              <span className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                © {new Date().getFullYear()} GuideMe by
              </span>
              <a 
                href="https://elevate.com.es" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="ml-1.5 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/images/logo/elevate.png" 
                  alt="Elevate" 
                  className="h-[14px] w-auto dark:hidden"
                />
                <img 
                  src="/images/logo/elevate-dark.png" 
                  alt="Elevate" 
                  className="h-[14px] w-auto hidden dark:block"
                />
              </a>
            </div>
          ) : (
            <div className="flex justify-center">
              <a 
                href="https://elevate.com.es" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:opacity-80 transition-opacity"
                title={`© ${new Date().getFullYear()} GuideMe by Elevate`}
              >
                <img 
                  src="/images/logo/logo-icon.svg" 
                  alt="Elevate" 
                  className="h-5 w-5 opacity-50 hover:opacity-70 transition-opacity"
                />
              </a>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default AppSidebar;
